/**
 * basement-cms storefront — handshake del ORIGEN de los feature flags entre la
 * app y el server SSR (card #636, fix del P0 #582).
 *
 * El problema (verificado en prod 2026-07-24): bajo ráfaga concurrente el SSR
 * resuelve los flags por fallback (`last-known-good`) y renderiza una home
 * **completa pero sin el carrusel de ofertas** — la feature opcional cae a
 * `fail-closed`. Esa página pasa el guard del edge (es una página completa, no
 * un frame de loading: el guard busca otra cosa **por diseño**) y **se cachea**
 * con `s-maxage=300`. Resultado medido: un solo render desafortunado al llenar
 * el cache deja la home sin ofertas **para todos los usuarios** durante ~6
 * minutos, y pasaba 12 veces por día.
 *
 * Angular no puede fijar headers de respuesta desde un componente ni desde un
 * servicio, así que se reusa **exactamente** el mecanismo que ya resuelve el 404
 * real (`shared/http-status.ts` ↔ `ssr/not-found.ts`): la app **marca el
 * documento** con un `<meta>`, y el server lee esa marca del HTML ya serializado
 * y la traduce a un header HTTP (`x-flags-source`), que es lo que el Worker del
 * edge mira para decidir si almacena la respuesta.
 *
 * POR QUÉ UN META Y NO SNIFFEAR EL TransferState DESDE EL WORKER: el Worker ya
 * intentaba eso (`readFeaturesFallback` en `storefront-edge-cache`) leyendo
 * `cms-features.fallback`, un campo que este servicio **dejó de emitir** cuando
 * pasó del booleano al enum `source`. Nadie se enteró: el campo de telemetría
 * viene devolviendo un `false` constante mientras el evento ocurría a diario.
 * Un contrato entre repos no puede depender de la forma interna del TransferState
 * de un framework. Acá el productor y el lector comparten esta constante y viven
 * en el mismo build: si alguien cambia la forma, rompe la compilación en el
 * mismo PR.
 */

/**
 * De dónde salió el mapa de flags con el que se renderizó la respuesta.
 *
 * Es la **única** definición del enum: `StorefrontFeatureService` la importa en
 * vez de declarar la suya, y `ssr/flags-source.ts` la usa para tipar lo que lee
 * del HTML. Un valor nuevo se agrega acá y las dos puntas lo ven.
 *
 * - `fresh` — el endpoint respondió. Lo que dice es la decisión del CEO.
 * - `last-known-good` — el endpoint falló; se sirve el último efectivo confirmado.
 * - `none` — falló y no había nada recordado (arranque en frío durante la caída).
 */
export type FlagsResolutionSource = 'fresh' | 'last-known-good' | 'none';

/** El ÚNICO valor que representa una resolución confirmada contra el backend. */
export const FLAGS_SOURCE_FRESH: FlagsResolutionSource = 'fresh';

/** Meta que transporta el origen de la resolución hasta el server SSR. */
export const CMS_FLAGS_SOURCE_META = 'cms-flags-source';
